import { Form } from 'antd';
import { FC, memo, useCallback, useState } from 'react';
import { useQueryClient } from 'react-query';
import { useNavigate } from 'react-router-dom';

import { AppMessage } from '@/common/components';
import { useAppPagination } from '@/common/hooks';
import { ApiRoutes, AppRoutes, BaseQueryParams, FormFieldsErrors } from '@/common/interfaces';
import { extractFormErrors, messageOnError } from '@/common/utils';

import { CloudflareAccount, CloudflareAccountsFormFields, CloudflareAccountsFormInputs, CloudflareAccountsQueryKeys, useCreateCloudflareAccountsMutation, useGetAllCloudflareAccountsQuery } from '@/modules/DomainsApi';
import { DomainsApiProviders } from '@/modules/DomainsApi/DomainsApi';

import { CloudflareAccountsFormView } from './CloudflareAccountsFormView';
import { cloudflareAccountsFormValidationSchema } from './validation';

export const CloudflareAccountsCreateFormContainer: FC = memo(() => {
  const navigate = useNavigate();
  const appMessageApi = AppMessage.useControls();
  const queryClient = useQueryClient();
  const { pageParam: page } = useAppPagination();
  const [form] = Form.useForm<CloudflareAccountsFormFields>();
  const [formErrors, setFormErrors] = useState<FormFieldsErrors<typeof CloudflareAccountsFormInputs>>(null);

  const { mutate: createDomainRegistrarMutation, isLoading: createDomainRegistrarsIsLoading } =
    useCreateCloudflareAccountsMutation()({
      onSuccess: (data: CloudflareAccount) => {
        queryClient.setQueryData<
        BaseQueryParams<typeof useGetAllCloudflareAccountsQuery>['data']
        >([CloudflareAccountsQueryKeys.GetAll, { page }], (prev) => ({
          meta: prev?.meta ?? { currentPage: 1, itemCount: 0, itemsPerPage: 1, totalItems: 0, totalPages: 1 },
          items: (prev?.items ? [{ email: data.email, id: data.id }, ...prev?.items] : prev?.items) ?? [],
        }));

        navigate(AppRoutes.DomainsApiCloudflareAccountsById.replace(':cloudflareAccountId', data.id));

        appMessageApi.success('Cloudflare акаунт успішно створено!');
      },
      onError: (err) => {
        setFormErrors(extractFormErrors<typeof CloudflareAccountsFormInputs>(err));
        messageOnError(err, appMessageApi);
      }
    });

  const initialValues: CloudflareAccountsFormFields = {
    email: undefined,
    password: undefined,
    apiKey: undefined,
    provider: DomainsApiProviders.Cloudflare
  };

  const handleFinish = useCallback((values: CloudflareAccountsFormFields): void => {
    setFormErrors(null);
    createDomainRegistrarMutation(values);
  }, []);

  const handleFinishFailed = useCallback((): void => {
    setFormErrors(null);
  }, []);

  const handleBack = useCallback((): void => {
    navigate(ApiRoutes.DomainsApiCloudflareAccounts);
  }, []);

  return (
    <CloudflareAccountsFormView
      formName="cloudflare-account-create"
      formType="create"
      form={ form }
      formErrors={ formErrors }
      initialValues={ initialValues }
      isFormLoading={ createDomainRegistrarsIsLoading }
      validationSchema={ cloudflareAccountsFormValidationSchema }
      handleBack={ handleBack }
      handleFinish={ handleFinish }
      handleFinishFailed={ handleFinishFailed }
    />
  );
});
